import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Plus, Edit2, Trash2, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { addressesApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import LockedCountrySelect from '../../components/LockedCountrySelect';
import { useLocalization } from '../../contexts/LocalizationContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const AddressesPage = () => {
  const { t, i18n } = useTranslation();
  const { countryCode } = useLocalization();
  const { toast } = useToast();
  // Addresses can only be saved for the storefront's selected country.
  const sameCountry = (a) => (a || '').toUpperCase() === (countryCode || '').toUpperCase();
  const countryName = useMemo(() => {
    try { return new Intl.DisplayNames([i18n.language || 'en'], { type: 'region' }).of((countryCode || '').toUpperCase()) || countryCode; }
    catch (e) { return countryCode; }
  }, [i18n.language, countryCode]);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState({
    type: 'shipping',
    fullName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: countryCode,
    phone: '',
    isDefault: false,
  });

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      const data = await addressesApi.getAddresses(token);
      setAddresses(data);
    } catch (error) {
      console.error('Error fetching addresses:', error);
      toast({
        title: t('common.error'),
        description: t('addresses.error.load'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (address = null) => {
    if (address) {
      setEditingAddress(address);
      setFormData(address);
    } else {
      setEditingAddress(null);
      setFormData({
        type: 'shipping',
        fullName: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zipCode: '',
        country: countryCode,
        phone: '',
        isDefault: false,
      });
    }
    setIsDialogOpen(true);
  };

  const handleSaveAddress = async () => {
    if (!sameCountry(formData.country)) {
      toast({
        title: t('common.error'),
        description: t('addressMore.wrongCountry', { country: countryName }),
        variant: 'destructive',
      });
      return;
    }
    try {
      const token = localStorage.getItem('accessToken');
      if (editingAddress) {
        await addressesApi.updateAddress(editingAddress.id, formData, token);
        toast({
          title: t('common.success'),
          description: t('addresses.success.updated'),
        });
      } else {
        await addressesApi.createAddress(formData, token);
        toast({
          title: t('common.success'),
          description: t('addresses.success.added'),
        });
      }
      setIsDialogOpen(false);
      await fetchAddresses();
    } catch (error) {
      console.error('Error saving address:', error);
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || t('addresses.error.save'),
        variant: 'destructive',
      });
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm(t('addresses.confirmDelete'))) return;

    try {
      const token = localStorage.getItem('accessToken');
      await addressesApi.deleteAddress(addressId, token);
      toast({
        title: t('common.success'),
        description: t('addresses.success.deleted'),
      });
      await fetchAddresses();
    } catch (error) {
      console.error('Error deleting address:', error);
      toast({
        title: t('common.error'),
        description: t('addresses.error.delete'),
        variant: 'destructive',
      });
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      const token = localStorage.getItem('accessToken');
      await addressesApi.setDefaultAddress(addressId, token);
      toast({
        title: t('common.success'),
        description: t('addresses.success.defaultSet'),
      });
      await fetchAddresses();
    } catch (error) {
      console.error('Error setting default address:', error);
      toast({
        title: t('common.error'),
        description: t('addresses.error.setDefault'),
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#be1f1a]"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-[#2C2420]">{t('addresses.title')}</h2>
        <Button onClick={() => handleOpenDialog()} className="bg-[#be1f1a] hover:bg-[#a71b17]">
          <Plus className="h-4 w-4 mr-2" />
          {t('addresses.addAddress')}
        </Button>
      </div>

      {addresses.length === 0 ? (
        <div className="text-center py-12">
          <MapPin className="h-20 w-20 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">{t('addresses.noAddresses')}</p>
          <Button onClick={() => handleOpenDialog()} className="bg-[#be1f1a] hover:bg-[#a71b17]">
            {t('addresses.addFirstAddress')}
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {addresses.map((address) => (
            <div
              key={address.id}
              className={`border rounded-lg p-4 relative ${
                address.isDefault ? 'border-[#be1f1a] bg-[#FAF8F5]' : ''
              } ${sameCountry(address.country) ? '' : 'opacity-70'}`}
            >
              {address.isDefault && (
                <div className="absolute top-2 right-2">
                  <span className="bg-[#be1f1a] text-white px-2 py-1 rounded text-xs font-semibold flex items-center gap-1">
                    <Star className="h-3 w-3" fill="currentColor" />
                    {t('addresses.default')}
                  </span>
                </div>
              )}
              
              <div className="mb-4 pr-20">
                <p className="font-semibold text-gray-900">{address.fullName}</p>
                <p className="text-sm text-gray-600 mt-1">{address.addressLine1}</p>
                {address.addressLine2 && (
                  <p className="text-sm text-gray-600">{address.addressLine2}</p>
                )}
                <p className="text-sm text-gray-600">
                  {address.city}, {address.state} {address.zipCode}
                </p>
                <p className="text-sm text-gray-600">{address.country}</p>
                {!sameCountry(address.country) && (
                  <p className="text-xs text-amber-700 mt-1" data-testid="address-other-country">
                    {t('addressMore.otherCountryAddress', { country: countryName })}
                  </p>
                )}
                <p className="text-sm text-gray-600 mt-2">{t('addresses.phone')}: {address.phone}</p>
              </div>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!sameCountry(address.country)}
                  onClick={() => handleOpenDialog(address)}
                >
                  <Edit2 className="h-3 w-3 mr-1" />
                  {t('addresses.edit')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDeleteAddress(address.id)}
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  {t('addresses.delete')}
                </Button>
                {!address.isDefault && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSetDefault(address.id)}
                  >
                    {t('addresses.setAsDefault')}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Address Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAddress ? t('addresses.editAddress') : t('addresses.addNew')}</DialogTitle>
            <DialogDescription>
              {editingAddress ? t('addresses.updateInfo') : t('addresses.addNewInfo')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="type">{t('addresses.addressType')}</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="shipping">{t('addresses.shipping')}</SelectItem>
                  <SelectItem value="billing">{t('addresses.billing')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="fullName">{t('addresses.fullName')} *</Label>
              <Input
                id="fullName"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="phone">{t('addresses.phone')} *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="addressLine1">{t('addresses.addressLine1')} *</Label>
              <AddressAutocomplete
                country={countryCode}
                onAddressSelect={(addressData) => {
                  setFormData({
                    ...formData,
                    addressLine1: addressData.street || '',
                    city: addressData.city || '',
                    state: addressData.state || '',
                    zipCode: addressData.postalCode || '',
                    country: countryCode
                  });
                }}
                placeholder={t('addresses.addressLine1')}
                value={formData.addressLine1}
              />
            </div>

            <div>
              <Label htmlFor="addressLine2">{t('addresses.addressLine2')}</Label>
              <Input
                id="addressLine2"
                value={formData.addressLine2}
                onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                placeholder={t('accountMore.addressLine2Placeholder')}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">{t('addresses.city')} *</Label>
                <Input
                  id="city"
                  value={formData.city}
                  readOnly
                  className="bg-gray-50 cursor-not-allowed"
                  placeholder={t('accountMore.autoFilled')}
                  required
                />
              </div>
              <div>
                <Label htmlFor="state">{t('addresses.state')} *</Label>
                <Input
                  id="state"
                  value={formData.state}
                  readOnly
                  className="bg-gray-50 cursor-not-allowed"
                  placeholder={t('accountMore.autoFilled')}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="zipCode">{t('addresses.zipCode')} *</Label>
                <Input
                  id="zipCode"
                  value={formData.zipCode}
                  readOnly
                  className="bg-gray-50 cursor-not-allowed"
                  placeholder={t('accountMore.autoFilled')}
                  required
                />
              </div>
              <div>
                <Label htmlFor="country">{t('addresses.country')} *</Label>
                <LockedCountrySelect id="country" />
                <p className="text-xs text-gray-500 mt-1">{t('checkoutMore.shipOnlyTo', { country: countryName })}</p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="isDefault"
                checked={formData.isDefault}
                onCheckedChange={(checked) => setFormData({ ...formData, isDefault: checked })}
              />
              <Label htmlFor="isDefault" className="font-normal cursor-pointer">
                {t('addresses.setDefault')}
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSaveAddress} className="bg-[#be1f1a] hover:bg-[#a71b17]">
              {editingAddress ? t('addresses.update') : t('addresses.add')} {t('account.addresses')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddressesPage;